/**
 * 图片优化工具
 * 用于优化 Sitecore 媒体 URL，提升加载性能
 */

/**
 * 响应式尺寸配置
 */
const RESPONSIVE_SIZES = {
  mobile: { width: 400, height: null }, // 更小的移动端尺寸
  tablet: { width: 768, height: null }, // 更小的平板尺寸
  desktop: { width: 1920, height: null },
  thumbnail: { width: 200, height: 200 }, // 更小的缩略图
  hero: { width: 1920, height: 1080 }, // 更小的Hero图片
};

/**
 * 优化图片 URL
 * @param {string} url - 原始图片 URL
 * @param {object} options - 优化选项
 * @returns {string} 优化后的 URL
 */
export const optimizeImageUrl = (url, options = {}) => {
  if (!url || typeof url !== 'string') return '';

  const {
    width = null,
    height = null,
    quality = 80,
    format = 'webp',
    device = 'desktop',
    crop = false,
  } = options;

  // 获取目标尺寸
  const targetSize = RESPONSIVE_SIZES[device] || RESPONSIVE_SIZES.desktop;
  const w = width || targetSize.width;
  const h = height || targetSize.height;

  // 构建 Sitecore Media Handler 参数
  const params = new URLSearchParams();

  if (w) params.append('mw', w); // Max width
  if (h) params.append('mh', h); // Max height
  if (quality) params.append('q', quality); // Quality
  if (crop) params.append('as', '1'); // Allow stretch
  if (format === 'webp') params.append('format', 'webp');

  // 检查是否是 Sitecore 媒体 URL
  if (url.includes('/-/media/')) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${params.toString()}`;
  }

  // 非 Sitecore URL，直接返回
  return url;
};

/**
 * 生成响应式图片 srcset
 * @param {string} url - 原始图片 URL
 * @param {object} options - 选项
 * @returns {string} srcset 字符串
 */
export const generateSrcSet = (url, options = {}) => {
  if (!url) return '';

  const { quality = 80, format = 'webp' } = options;

  return [
    `${optimizeImageUrl(url, { width: 640, quality, format, device: 'mobile' })} 640w`,
    `${optimizeImageUrl(url, { width: 1024, quality, format, device: 'tablet' })} 1024w`,
    `${optimizeImageUrl(url, { width: 1920, quality, format, device: 'desktop' })} 1920w`,
    `${optimizeImageUrl(url, { width: 2560, quality, format, device: 'hero' })} 2560w`,
  ].join(', ');
};

/**
 * 生成 sizes 属性
 * @param {string} breakpoints - 断点配置
 * @returns {string} sizes 字符串
 */
export const generateSizes = (breakpoints = 'default') => {
  const sizesMap = {
    default:
      '(max-width: 640px) 640px, (max-width: 1024px) 1024px, (max-width: 1920px) 1920px, 2560px',
    hero: '100vw',
    thumbnail: '(max-width: 640px) 150px, (max-width: 1024px) 250px, 300px',
    card: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
  };

  return sizesMap[breakpoints] || sizesMap.default;
};

/**
 * 预加载关键图片
 * @param {string} url - 图片 URL
 * @param {object} options - 选项
 */
export const preloadImage = (url, options = {}) => {
  if (!url || typeof window === 'undefined') return;

  const { priority = 'high', device = 'desktop' } = options;

  // 创建 link 元素进行预加载
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = optimizeImageUrl(url, { device });
  link.imageSrcset = generateSrcSet(url);
  link.imageSizes = generateSizes('default');

  if (priority === 'high') {
    link.fetchpriority = 'high';
  }

  document.head.appendChild(link);
};

/**
 * 批量预加载图片
 * @param {string[]} urls - 图片 URL 数组
 * @param {object} options - 选项
 */
export const preloadImages = (urls, options = {}) => {
  if (!Array.isArray(urls) || urls.length === 0) return;

  // 使用 requestIdleCallback 在浏览器空闲时预加载
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      urls.forEach((url) => preloadImage(url, options));
    });
  } else {
    // 降级方案：延迟预加载
    setTimeout(() => {
      urls.forEach((url) => preloadImage(url, options));
    }, 1000);
  }
};

/**
 * 从 Sitecore 字段提取图片 URL
 * @param {object} imageField - Sitecore 图片字段
 * @param {string} device - 设备类型
 * @returns {string} 图片 URL
 */
export const extractImageUrl = (imageField, device = 'desktop') => {
  if (!imageField) return '';

  // Sitecore 响应式图片字段结构
  if (imageField.fields) {
    const { desktopImage, tabletImage, mobileImage } = imageField.fields;

    switch (device) {
      case 'mobile':
        return mobileImage?.value?.src || tabletImage?.value?.src || desktopImage?.value?.src || '';
      case 'tablet':
        return tabletImage?.value?.src || desktopImage?.value?.src || '';
      case 'desktop':
      default:
        return desktopImage?.value?.src || '';
    }
  }

  // 标准 Sitecore 图片字段
  if (imageField.value?.src) {
    return imageField.value.src;
  }

  // 直接 URL
  if (typeof imageField === 'string') {
    return imageField;
  }

  return '';
};

/**
 * 检查图片是否可以优化
 * @param {string} url - 图片 URL
 * @returns {boolean} 是否可优化
 */
export const isOptimizable = (url) => {
  if (!url || typeof url !== 'string') return false;

  // Sitecore 媒体库图片可以优化
  if (url.includes('/-/media/')) return true;

  // 本地静态资源可以优化
  if (url.startsWith('/public/') || url.startsWith('/images/')) return true;

  return false;
};

/**
 * 获取图片占位符（LQIP - Low Quality Image Placeholder）
 * @param {string} url - 原始图片 URL
 * @returns {string} 占位符 URL
 */
export const getPlaceholder = (url) => {
  if (!url) return '';

  // 生成极小的占位图（用于渐进式加载）
  return optimizeImageUrl(url, {
    width: 20,
    quality: 10,
    device: 'thumbnail',
  });
};

export default {
  optimizeImageUrl,
  generateSrcSet,
  generateSizes,
  preloadImage,
  preloadImages,
  extractImageUrl,
  isOptimizable,
  getPlaceholder,
};
